<script setup lang="ts">
import { inject, onMounted, ref } from 'vue';
import { request } from '@/util/request';
import { WindowTool } from '@/components/common/window/WindowInterface';
import ScTree from '@/components/common/tree/ScTree.vue';
import TranReduce from '@/components/common/transition/TranReduce.vue';
import { DataNode } from '@/components/common/tree/TreeUtil';
import DeviceNode from './DeviceNode.vue';
import ProxyNode from './ProxyNode.vue';

const props = defineProps<{
  proxies: any[];
  devices: any[];
  num: number;
}>();
const remainingDevices = ref<any[]>([]);

const windowTool = inject<WindowTool | false>(WindowTool, false);

const tree = ref<InstanceType<typeof ScTree>>();
onMounted(() => {
  const perNum = Number(props.num);
  let currentIndex = 0;
  const data = props.proxies.map((proxy) => {
    const devices = props.devices.slice(currentIndex, currentIndex + perNum);
    currentIndex += perNum;
    return {
      id: proxy.id,
      data: proxy,
      opened: true,
      children: devices.map((device) => {
        return {
          id: device.id,
          data: device,
          children: [],
        };
      }),
    };
  });
  remainingDevices.value = props.devices.slice(currentIndex);
  tree.value.setData({
    id: 1,
    opened: true,
    data: {
      name: 'AlignLeftOutlined',
    },
    children: data,
  });
});

function remove( node: DataNode<any>) {
  remainingDevices.value.push(node.data);
  node.del();
}

function onDrag(
  ev: { data: { id: string }; source: string },
  target: DataNode<unknown>,
  pos: 'before' | 'after' | 'inside'
) {
  if (ev.data.id === target.id) {
    return;
  }
  if (tree.value) {
    console.log(ev, target);
    if (ev.source === 'remaining') {
      tree.value.getRoot().addChild({
        id: ev.data.id,
        data: ev.data,
      });
      remainingDevices.value.splice(
        remainingDevices.value.findIndex((i) => i.id === ev.data.id),
        1
      );
    }
    tree.value.moveNodeTo(ev.data.id, target.id, pos);
    if (pos === 'inside') {
      target.setOpen(true);
    } else if (target.parent) {
      target.parent.setOpen(true);
    }
  }
}

function close() {
  if (windowTool) {
    windowTool.close(true);
  }
}

async function submit() {
  if (!tree.value) {
    return;
  }
  const data = tree.value?.getRoot()?.children.map((i) => {
    return {
      id: i.id,
      devices: i.children.map((i) => i.id),
    };
  });
  const res = await request('management/proxy/bind', { data });
  if (res) {
    close();
  }
}

const columns = [
  {
    title: '代理信息',
    dataIndex: 'data',
    key: 'data',
  },
  {
    title: '结果',
    dataIndex: 'err',
    key: 'err',
  },
];
</script>
<template>
  <div class="form-box">
    <form class="form">
      <div class="form-main">
        <div class="desc">绑定情况如下，可以拖动设备调整线路</div>
        <div class="form-body">
          <div class="left">
            <div class="title">绑定情况</div>
            <sc-tree ref="tree">
              <template v-slot="{ node, children }">
                <proxy-node :node="node" v-if="node.level === 1" @move="onDrag(...$event)"/>
                <device-node
                  :level="node.level"
                  :remove="() => remove(node)"
                  source="tree"
                  :data="node.data"
                  v-if="node.level === 2"
                />
                <tran-reduce :show="node.opened">
                  <div class="children">
                    <template v-for="(child, index) in children" :key="index">
                      <component :is="child" />
                    </template>
                  </div>
                </tran-reduce>
              </template>
            </sc-tree>
          </div>
          <div class="right">
            <div class="title" style="color: red">
              待绑定设备
            </div>
            <device-node
              :level="0"
              source="remaining"
              :data="device"
              v-for="(device, k) in remainingDevices"
              :key="k"
            />
          </div>
        </div>
      </div>
      <div class="form-btn-group">
        <sc-btn type="primary" :handler="submit" text="绑定" />
      </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.form-box {
  display: flex;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
}

.form {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  max-width: 1200px;

  .form-main {
    flex-grow: 1;
    overflow: auto;
    padding: $spacing-medium $spacing-extra-large;

    .desc {
      color: $color-content-text-secondary;
      padding-bottom: 15px;
    }

    .form-body {
      flex-grow: 1;
      overflow: auto;
      display: flex;
      flex-direction: row;

      .left,
      .right {
        .title {
          margin-bottom: $spacing-small;
        }
      }
      .left {
        flex: 3;
      }

      .right {
        flex: 2;
        padding-left: $spacing-extra-large;
      }

      .form-group {
        padding-bottom: $spacing-extra-large;

        .form-group-title {
          padding-bottom: 15px;
          font-size: $font-size-medium;
          font-weight: $font-weight-primary;
        }

        .label {
          padding-bottom: 15px;
        }

        .desc {
          color: $color-content-text-secondary;
          padding-bottom: 15px;
        }
      }
    }
  }

  .form-btn-group {
    border-top: 1px solid $color-content-border;
    padding: $spacing-medium;
  }
}
</style>
